import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSButton from "views/system-design/DSButton";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSTable from "views/system-design/Table/DSTable";
import DSTableHead from "views/system-design/Table/DSTableHead";
import DSTableBody from "views/system-design/Table/DSTableBody";
import DSTableRow from "views/system-design/Table/DSTableRow";
import DSTableCell from "views/system-design/Table/DSTableCell";
import DSTableContainer from "views/system-design/Table/DSTableContainer";
import DSTextField from "views/system-design/DSTextField";
import { FeatureBetaGroup } from "../../../api/admin";
import {
  useFeatureBetaGroupClocks,
  useRemoveClocksFromFeatureBetaGroup,
} from "../hooks/useFeatureBetaGroups";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 400;

// Holds a value back until it stops changing, so each keystroke in the search
// box does not fire its own BigQuery job.
const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
};

interface GroupMembersDialogProps {
  open: boolean;
  onClose: () => void;
  group: FeatureBetaGroup | null;
}

const formatHeartbeat = (heartbeat: unknown) => {
  if (!heartbeat) return "—";
  const date = new Date(heartbeat as string);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const GroupMembersDialog = ({
  open,
  onClose,
  group,
}: GroupMembersDialogProps) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  // The request is debounced so typing does not fire a BigQuery job per key.
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const { data, isLoading, isFetching } = useFeatureBetaGroupClocks(
    open && group ? group.id : null,
    {
      skip: page * PAGE_SIZE,
      limit: PAGE_SIZE,
      search: debouncedSearch.trim() || undefined,
    },
  );
  const { mutate: removeClocks, isPending: isRemoving } =
    useRemoveClocksFromFeatureBetaGroup();

  useEffect(() => {
    if (open) {
      setPage(0);
      setSearch("");
    }
  }, [open, group?.id]);

  // A narrower result set can leave the current page past the end.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const total = data?.total ?? 0;
  // Until the first page lands, the list endpoint's count stands in.
  const titleCount = data?.total ?? group?.memberCount ?? 0;
  const clocks = data?.clocks ?? [];
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  // Removing the last clock on a page would otherwise leave the dialog on an
  // empty page reading "51-50 of 50".
  useEffect(() => {
    if (total > 0 && page > lastPage) setPage(lastPage);
  }, [total, page, lastPage]);

  const handleRemove = (clockId: string) => {
    if (!group) return;
    removeClocks({ id: group.id, clockIds: [clockId] });
  };

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DSDialogTitle>
        {group
          ? `${group.name} — ${titleCount} clock${titleCount === 1 ? "" : "s"}`
          : "Clocks"}
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ pt: 1, pb: 2 }}>
          <DSTextField
            size="small"
            fullWidth
            placeholder="Search by IDF or clock ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </DSBox>
        {isLoading ? (
          <DSBox sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <DSCircularProgress />
          </DSBox>
        ) : total === 0 ? (
          <DSTypography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {debouncedSearch.trim()
              ? `No clock in this group matches "${debouncedSearch.trim()}".`
              : 'No clocks yet. Add them from the Devices page: filter, select, then "Add to beta group".'}
          </DSTypography>
        ) : (
          <DSTableContainer>
            <DSTable size="small">
              <DSTableHead>
                <DSTableRow>
                  <DSTableCell>IDF</DSTableCell>
                  <DSTableCell>Clock ID</DSTableCell>
                  <DSTableCell>Version</DSTableCell>
                  <DSTableCell>Last heartbeat</DSTableCell>
                  <DSTableCell align="right">Actions</DSTableCell>
                </DSTableRow>
              </DSTableHead>
              <DSTableBody>
                {clocks.map((clock) => (
                  <DSTableRow key={clock.id}>
                    <DSTableCell>{clock.idf ?? "—"}</DSTableCell>
                    <DSTableCell>
                      <DSTypography variant="caption">{clock.id}</DSTypography>
                    </DSTableCell>
                    <DSTableCell>{clock.version ?? "—"}</DSTableCell>
                    <DSTableCell>
                      <DSTypography variant="caption">
                        {formatHeartbeat(clock.heartbeat)}
                      </DSTypography>
                    </DSTableCell>
                    <DSTableCell align="right">
                      <DSButton
                        size="small"
                        color="error"
                        disabled={isRemoving}
                        onClick={() => handleRemove(clock.id)}
                      >
                        Remove
                      </DSButton>
                    </DSTableCell>
                  </DSTableRow>
                ))}
              </DSTableBody>
            </DSTable>
          </DSTableContainer>
        )}
      </DSDialogContent>
      <DSDialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isFetching && <DSCircularProgress size={16} />}
          <DSTypography variant="caption" color="text.secondary">
            {total === 0
              ? ""
              : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`}
          </DSTypography>
        </DSBox>
        <DSBox sx={{ display: "flex", gap: 1 }}>
          <DSButton
            size="small"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </DSButton>
          <DSButton
            size="small"
            disabled={page >= lastPage}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </DSButton>
          <DSButton
            size="small"
            variant="outlined"
            disabled={!group?.memberCount}
            onClick={() =>
              group && navigate(`/admin/devices?featureBetaGroup=${group.id}`)
            }
          >
            See devices
          </DSButton>
          <DSButton size="small" variant="contained" onClick={onClose}>
            Close
          </DSButton>
        </DSBox>
      </DSDialogActions>
    </DSDialog>
  );
};

export default GroupMembersDialog;
